import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building, 
  MapPin, 
  Search, 
  Filter,
  Star,
  Globe,
  BadgeCheck,
  Plus,
  ChevronRight,
  Users
} from 'lucide-react';
import { API, useAuth } from '@/App';

const Businesses = () => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isExpatOwned, setIsExpatOwned] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [showFilters, setShowFilters] = useState(false);
  const headerRef = useRef(null);

  // Cache list state across route navigation so back from a business detail
  // lands the user instantly on the same list at the page header.
  useListStatePersistence({
    key: 'businesses',
    state: { businesses, total, search, category, isExpatOwned, sortBy },
    enabled: !loading && businesses.length > 0,
    onRestore: (cached) => {
      if (Array.isArray(cached.businesses)) setBusinesses(cached.businesses);
      if (typeof cached.total === 'number') setTotal(cached.total);
      if (typeof cached.search === 'string') setSearch(cached.search);
      if (typeof cached.category === 'string') setCategory(cached.category);
      if (typeof cached.isExpatOwned === 'string') setIsExpatOwned(cached.isExpatOwned);
      if (typeof cached.sortBy === 'string') setSortBy(cached.sortBy);
      setLoading(false);
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });

  useEffect(() => {
    fetchCategories();
    fetchBusinesses();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBusinesses();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, category, isExpatOwned, sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/businesses/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (isExpatOwned === 'yes') params.append('is_expat_owned', 'true');
      params.append('sort_by', sortBy);
      
      const response = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(response.data.businesses);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setIsExpatOwned('');
    setSortBy('created_at');
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
              <Building className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" ref={headerRef}>
              Business Directory
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover expat-friendly businesses and services. From relocation help to healthcare, 
              find trusted providers recommended by the community.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search businesses, services..."
                  className="h-14 pl-12 pr-4 rounded-full border-2 text-lg"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-14 px-6 rounded-full border-2"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filters
              </Button>
              {user && (
                <Link to="/businesses/register">
                  <Button className="h-14 px-6 rounded-full bg-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Business
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl p-6 border-2 border-border mb-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={isExpatOwned || "all"} onValueChange={(v) => setIsExpatOwned(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ownership" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Businesses</SelectItem>
                    <SelectItem value="yes">Expat-Owned Only</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sort By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Newest First</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="name">Alphabetical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={clearFilters}>Clear All Filters</Button>
              </div>
            </motion.div>
          )}

          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-muted-foreground">
              {total} {total === 1 ? 'business' : 'businesses'} found
            </p>
          </div>

          {/* Business Listings */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : businesses.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border-2 border-border text-center">
              <Building className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No businesses found</h3>
              <p className="text-muted-foreground mb-6">
                Be the first to add a business to our directory!
              </p>
              {user && (
                <Link to="/businesses/register">
                  <Button className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Register Your Business
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business, index) => (
                <motion.div
                  key={business.business_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link to={`/businesses/${business.business_id}`}>
                    <div className="bg-white rounded-2xl border-2 border-border hover:border-primary/50 transition-all group overflow-hidden h-full">
                      {/* Cover/Logo */}
                      <div className="h-32 bg-gradient-to-br from-primary/10 to-accent/10 relative">
                        {business.cover_image && (
                          <img src={business.cover_image} alt="" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute -bottom-8 left-4">
                          <div className="w-16 h-16 bg-white rounded-xl border-2 border-border flex items-center justify-center overflow-hidden">
                            {business.logo ? (
                              <img src={business.logo} alt={business.name} className="w-full h-full object-cover" />
                            ) : (
                              <Building className="w-8 h-8 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="p-4 pt-12">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-bold text-lg group-hover:text-primary transition-colors line-clamp-1">
                              {business.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">{business.category}</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {business.is_verified && (
                              <BadgeCheck className="w-5 h-5 text-blue-500" />
                            )}
                            {business.is_expat_owned && (
                              <Globe className="w-5 h-5 text-accent" />
                            )}
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {business.description}
                        </p>

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-2">
                            {renderStars(business.average_rating)}
                            <span className="text-sm text-muted-foreground">
                              ({business.review_count})
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            {business.city}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA for non-logged-in users */}
          {!user && (
            <div className="mt-12 bg-gradient-to-r from-primary to-accent rounded-3xl p-8 text-center text-white">
              <h2 className="text-2xl font-bold mb-4">Own a Business?</h2>
              <p className="text-white/90 mb-6 max-w-lg mx-auto">
                Sign in to list your business in our directory and reach the expat community.
              </p>
              <Link to="/">
                <Button className="bg-white text-primary hover:bg-white/90 rounded-full font-bold px-8">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Businesses;
